/**
 * screening-criteria.ts
 *
 * The ONE place that decides which screening criteria a channel may show and how they read
 * (leasing-support BR-102 / BR-103 / BR-104). Every consumer — the public listing page, the
 * application form, the HotPads feed / Zillow RealTime push and ShowMojo — goes through these
 * helpers, so the published field set, the row labels and the plain-text block never drift
 * between channels.
 */
import { SMOKING_POLICY_LABELS, labelFor } from './leasing-labels.js';
/**
 * THE publish gate (BR-102). Returns null when there are no criteria or the landlord turned
 * publishing off; otherwise a NEW object holding exactly the seven publishable fields (the
 * flag itself and any other column on the input never pass through).
 */
export function selectPublishedCriteria(c) {
    if (!c || !c.publishScreeningCriteria)
        return null;
    return {
        minIncomeMultiple: c.minIncomeMultiple,
        maxOccupants: c.maxOccupants,
        smokingPolicy: c.smokingPolicy,
        minCreditScore: c.minCreditScore,
        acceptsHousingVouchers: c.acceptsHousingVouchers,
        criminalHistoryLookbackYears: c.criminalHistoryLookbackYears,
        additionalCriteria: c.additionalCriteria,
    };
}
/**
 * Display rows in channel order. Unset optional fields are omitted; housing vouchers always
 * renders. The income row carries the derived monthly dollar figure only when the asking rent
 * is known and positive.
 */
export function getCriteriaDisplay(c, askingRentCents) {
    if (!c)
        return { rows: [], additionalCriteria: null };
    const rows = [];
    if (c.minIncomeMultiple != null) {
        let value = `${c.minIncomeMultiple}× monthly rent`;
        if (askingRentCents != null && askingRentCents > 0) {
            const dollars = Math.round((askingRentCents / 100) * c.minIncomeMultiple);
            value += ` ($${dollars.toLocaleString('en-US')}/mo)`;
        }
        rows.push({ key: 'income', label: 'Income', value });
    }
    if (c.minCreditScore != null) {
        rows.push({ key: 'credit', label: 'Credit score', value: `${c.minCreditScore} minimum` });
    }
    if (c.maxOccupants != null) {
        rows.push({ key: 'occupants', label: 'Max occupants', value: String(c.maxOccupants) });
    }
    if (c.smokingPolicy) {
        rows.push({
            key: 'smoking',
            label: 'Smoking',
            value: labelFor(SMOKING_POLICY_LABELS, c.smokingPolicy),
        });
    }
    rows.push({
        key: 'vouchers',
        label: 'Housing vouchers',
        value: c.acceptsHousingVouchers ? 'Accepted' : 'Not accepted',
    });
    if (c.criminalHistoryLookbackYears != null) {
        rows.push({
            key: 'criminalHistory',
            label: 'Criminal history',
            value: `Last ${c.criminalHistoryLookbackYears} years considered`,
        });
    }
    const additional = c.additionalCriteria?.trim();
    return { rows, additionalCriteria: additional ? additional : null };
}
/**
 * Plain-text "Screening criteria" block for free-text syndication fields (BR-103). No XML/JSON
 * escaping — the caller's serializer escapes. Returns '' when there is nothing to show.
 */
export function renderScreeningCriteriaText(d) {
    if (d.rows.length === 0 && !d.additionalCriteria)
        return '';
    const lines = d.rows.map((r) => `${r.label}: ${r.value}`);
    let text = `Screening criteria\n${lines.join('\n')}`;
    if (d.additionalCriteria)
        text += `\n${d.additionalCriteria}`;
    return text;
}
/**
 * Append a rendered block to a free-text description. An empty block leaves the description
 * untouched; an empty/absent description yields the block alone.
 */
export function appendScreeningCriteriaText(description, block) {
    if (!block)
        return description ?? null;
    if (!description || description.trim() === '')
        return block;
    return `${description}\n\n${block}`;
}
//# sourceMappingURL=screening-criteria.js.map